<template>
  <SoWidget plain no-border :loading="usageQuery?.isFetching">
    <section class="usage-calculator pa-1">
      <h5 class="mb-3" data-testid="read-usage-title">
        {{ meterpoint.eacOrAq }} Estimator
        <span
          v-if="meterpoint.isElectricity && register.rateName"
          class="text-h5"
        >
          ({{ register.rateName }})
        </span>
      </h5>
      <div
        v-if="usageQuery?.isFetched"
        class="mb-3"
        data-testid="read-usage-breakdown"
      >
        <p class="font-weight-medium">EAC {{ usage }} kWh</p>
        <p>
          {{ usageQuery.data.usageBetweenReadsFormatted }}
          <span v-if="!meterpoint.isElectricity"
            >(~{{ usageQuery.data.usageBetweenReadsInKwhFormatted }})
          </span>
          over {{ usageQuery.data.daysBetweenReads }} days (ALP:
          {{ usageQuery.data.annualLoadProfileRangeFormatted }})
        </p>
      </div>
      <SoAlert
        v-if="usageQuery?.isError"
        title="Failed to fetch reading calculation"
        type="error"
      />
      <SoSelect
        ref="older-reading-select"
        v-model="olderReadingId"
        :items="toSelectOptions(nonEstimatedReadingsOrdered)"
        data-testid="older-reading-select"
        type="simple"
        class="mb-5"
        label="Reading 1"
        autofocus
      />
      <SoSelect
        ref="newer-reading-select"
        v-model="newerReadingId"
        :items="toSelectOptions(remainingReadings)"
        :disabled="!olderReadingId"
        data-testid="newer-reading-select"
        type="simple"
        label="Reading 2"
      />
      <SoAlert
        v-if="required && (!olderReadingId || !newerReadingId)"
        type="warning"
        class="mt-2"
        >Please select readings</SoAlert
      >
    </section>
  </SoWidget>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import SoWidget from "@/components/SoWidget.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import { IRegister } from "@/models/meterRegister/meterRegister.types"
  import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
  import { formatDate } from "@/helpers/dateHelper"
  import { MeterpointService } from "@/services/MeterpointService"

  export default defineComponent({
    components: { SoAlert, SoSelect, SoWidget },
    props: {
      meterpoint: {
        type: Object as PropType<IMeterpoint>,
        required: true,
      },
      register: {
        type: Object as PropType<IRegister>,
        required: true,
      },
      required: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["updated"],
    data() {
      return {
        olderReadingId: null,
        newerReadingId: null,
      }
    },
    computed: {
      olderReading() {
        return this.nonEstimatedReadingsOrdered.find(
          (reading) => reading.id == this.olderReadingId,
        )
      },
      newerReading() {
        return this.nonEstimatedReadingsOrdered.find(
          (reading) => reading.id == this.newerReadingId,
        )
      },
      nonEstimatedReadingsOrdered() {
        return this.register.readings
          .filter((reading) => !reading.isEstimated)
          .sort((reading1, reading2) =>
            reading1.date.isAfter(reading2.date) ? 1 : -1,
          )
      },
      remainingReadings() {
        if (!this.olderReading) return []
        return this.nonEstimatedReadingsOrdered.filter((reading) =>
          reading.date.isAfter(this.olderReading?.date),
        )
      },
      usageQuery() {
        if (!this.olderReading || !this.newerReading) return

        return MeterpointService.getUsageFromReadingDifference(
          this.olderReading,
          this.newerReading,
        )
      },
      usage() {
        return this.usageQuery?.data.annualUsage
      },
    },
    watch: {
      olderReading(olderReading) {
        const selectedReadingAfterNewer =
          this.newerReading && olderReading.date.isAfter(this.newerReading.date)

        if (selectedReadingAfterNewer) {
          this.newerReadingId = null
        }
      },
      usage(eac) {
        this.$emit("updated", eac ?? 0)
      },
    },
    methods: {
      toSelectOptions(readings) {
        return readings.map((reading) => ({
          title: `${reading.cumulative} on ${formatDate(reading.date)}`,
          value: String(reading.id),
        }))
      },
    },
  })
</script>

<style scoped>
  .usage-calculator {
    line-height: 1.6;
  }
</style>
